import {
    Box, Button, Container, Divider, IconButton, InputAdornment,
    Stack, TextField, Typography, Avatar, MenuItem, Skeleton,
    Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions,
    Fab,
    Link,
    Collapse
} from "@mui/material";
import { Edit, Delete, Preview, Search, Add } from '@mui/icons-material';
import axios from "axios";
import { useEffect, useState } from "react";
import uploadFileToCloud from "../utils/upload-file";
import ToastMessage from "../components/toast-message";
import { useNavigate } from "react-router-dom";
import HomeNavbar from "../components/home-nav-bar";

function AuthorDashboard() {
    const navigate = useNavigate();

    const [profileDetails, setProfileDetails] = useState({
        id: '', name: '', email: '', phone_number: '',
        institution: '', bio: '', profile_image: ''
    });

    const [dp, setDp] = useState(null);
    const [manuscripts, setManuscripts] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [loadingManuscripts, setLoadingManuscripts] = useState(true);
    const [status, setStatus] = useState({ type: 0, message: '' });

    const [openDialog, setOpenDialog] = useState(false);
    const [deletionReason, setDeletionReason] = useState('');
    const [selectedManuscriptId, setSelectedManuscriptId] = useState(null);

    const [openRejectionNote, setOpenRejectionNote] = useState(false);

    const handleToggleRejectionNote = () => {
        setOpenRejectionNote((prev) => !prev);
    };

    const clearToastMessge = () => setStatus({ type: 0, message: '' });

    const getStatusColor = (status) => {
        switch (status) {
            case 0:
                return '#FFEB3B'; // Yellow for "In Review"
            case 1:
                return '#388E3C'; // Green for "Published"
            case 2:
                return '#D32F2F'; // Red for "Rejected"
            case 3:
                return '#4cd1e6'; // Red for "Rejected"
            default:
                return 'text.secondary'; // Default color
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setDp(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileDetails((prev) => ({ ...prev, profile_image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
        e.target.value = null;
    };

    const update_author_profile = async () => {
        try {
            setLoading(true);
            let updatedProfile = { ...profileDetails };
            if (dp) {
                const path = `wajls/profile_pictures/${profileDetails.id}/${dp.name}`;
                const downloadURL = await uploadFileToCloud(dp, path, (progress) => {
                    console.log(`Upload Progress: ${progress.toFixed(2)}%`);
                });
                updatedProfile.profile_image = downloadURL;
            }
            const response = await axios.post(
                "https://api.wajls.com/client/v1/update_author_profile.php",
                updatedProfile
            );
            if (response.data.success) {
                setStatus({ type: 1, message: 'Profile updated' });
                setProfileDetails(updatedProfile);
            } else {
                setStatus({ type: 0, message: response.data.error });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const get_author_details = async (user_id) => {
        try {
            setLoadingProfile(true);
            setLoadingManuscripts(true);
            const response = await axios.get(`https://api.wajls.com/client/v1/get_author.php?author_id=${user_id}`);
            
            const result = response.data;
            
            const { id, name, email, phone_number, institution, bio, profile_image } = result.author;
            const cleanedBio = bio ?? "";
            
            setProfileDetails({ 
                id: id,
                name: name, 
                email: email,
                phone_number: phone_number, 
                institution: institution, 
                bio: cleanedBio, 
                profile_image: profile_image
            });
            setManuscripts(result.manuscripts);
            
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingProfile(false);
            setLoadingManuscripts(false);
        }
    };

    const makePayment = async (article_id) => {
        const payload = {
            username: profileDetails.name,
            email: profileDetails.email,
            article_id: article_id
        };
        
        try {
            setLoading(true);
            const response = await axios.post("https://api.wajls.com/client/v1/get_payment_url.php", payload);
            window.open(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    
        
    }

    const openDeleteDialog = (manuscriptId) => {
        setSelectedManuscriptId(manuscriptId);
        setDeletionReason('');
        setOpenDialog(true);
    };

    const submitDeletionRequest = async () => {
        try {
            setLoading(true);
            const payload = {
                author_id: profileDetails.id,
                article_id: selectedManuscriptId,
                reason: deletionReason
            };
            const response = await axios.post(
                "https://api.wajls.com/client/v1/request_article_deletion.php",
                payload
            );
            
            if (response.data.success) {
                setStatus({ type: 1, message: "Deletion request submitted" });
            } else {
                setStatus({ type: 0, message: response.data.error });
            }
        } catch (err) {
            console.error(err);
            setStatus({ type: 0, message: "Failed to submit deletion request" });
        } finally {
            setLoading(false);
            setOpenDialog(false);
        }
    };

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.id) get_author_details(user.id);
    }, []);

    const handleInputChange = (e) => {
        setProfileDetails({
            ...profileDetails,
            [e.target.name]: e.target.value,
        });
    };

    const filteredManuscripts = manuscripts.filter(m => {
        return (
            (!statusFilter || m.status === statusFilter) &&
            (!searchTerm || m.title.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    });

    return (
        <Box sx={{ backgroundColor: 'grey.100', minHeight: '100vh' }}>
            <HomeNavbar/>

            <Container>
                <Stack direction={{ xs: 'column', md: 'row' }} sx={{  position: 'relative' }}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 2, my: 4, borderColor: '#1976d2', borderWidth: '2px' }} />}
                >
                    {/* Profile Section */}
                    <Box sx={{ flex: 1, p: 2 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                borderBottom: "1px solid #e0e0e0",
                                mb: 2,
                            }}
                        >
                            Profile Details
                        </Typography>
                        {loadingProfile ? (
                            <Stack spacing={2}>
                                <Skeleton variant="circular" width={80} height={80} />
                                {[...Array(5)].map((_, i) => <Skeleton key={i} variant="text" height={56} />)}
                                <Skeleton variant="rectangular" height={40} width="40%" />
                            </Stack>
                        ) : (
                            <>
                                <Box display="flex" alignItems="center" gap={2} mb={3}>
                                    <Avatar src={profileDetails.profile_image} sx={{ width: 80, height: 80, border: '1px solid #000000' }} />
                                    <IconButton color="primary" component="label">
                                        <Edit />
                                        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => {
                                        setDp(null);
                                        setProfileDetails(prev => ({ ...prev, profile_image: '' }));
                                    }}>
                                        <Delete />
                                    </IconButton>
                                </Box>

                                <Stack spacing={2}>
                                    <TextField name="name" label="Full Name" value={profileDetails.name} onChange={handleInputChange} fullWidth sx={{ background: '#fff' }} />
                                    <TextField name="email" label="Email" value={profileDetails.email} fullWidth sx={{ background: '#fff' }} disabled />
                                    <TextField name="phone_number" label="Phone Number" value={profileDetails.phone_number} onChange={handleInputChange} fullWidth sx={{ background: '#fff' }} />
                                    <TextField name="institution" label="Institution" value={profileDetails.institution} onChange={handleInputChange} fullWidth sx={{ background: '#fff' }} />
                                    <TextField
                                        name="bio"
                                        label="Bio"
                                        value={profileDetails.bio}
                                        onChange={handleInputChange}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        sx={{ background: '#fff' }}
                                        inputProps={{ maxLength: 200 }}
                                        helperText={`${profileDetails.bio.length}/200`}
                                    />
                                </Stack>

                                <Stack direction="row" spacing={2} mt={3}>
                                    <Button variant="contained" color="primary" onClick={update_author_profile}>Update Profile</Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Preview />}
                                        onClick={() =>
                                            window.open(`/author?id=${profileDetails.id}&name=${encodeURIComponent(profileDetails.name)}`, '_blank', 'noopener,noreferrer')
                                        }
                                        >
                                        Preview
                                    </Button>
                                    <Button variant="contained" color="error" onClick={() => {
                                       localStorage.removeItem('user');
                                       navigate('/auth') 
                                    }}>Logout</Button>

                                </Stack>
                            </>
                        )}
                    </Box>

                    {/* Manuscripts */}
                    <Box sx={{ flex: 1, p: 2 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                borderBottom: "1px solid #e0e0e0",
                                mb: 2,
                            }}
                        >
                            Manuscripts
                        </Typography>

                        <Stack direction="row" spacing={2} mb={2}>
                            <TextField
                                select
                                label="Filter by Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                fullWidth sx={{ background: '#fff' }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Pending Review">Pending Review</MenuItem>
                                <MenuItem value="In Review">In Review</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                            </TextField>
                            <TextField
                                label="Search by Title"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                fullWidth sx={{ background: '#fff' }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>

                        {loadingManuscripts ? (
                            <Stack spacing={2}>
                                {[...Array(3)].map((_, index) => (
                                    <Skeleton key={index} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                                ))}
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                {filteredManuscripts.map((manuscript, index) => (
                                    <Box
                                    key={index}
                                    sx={{
                                      p: 2,
                                      background: '#fff',
                                      borderRadius: 2,
                                      boxShadow: 1,
                                      mb: 2
                                    }}
                                  >
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {manuscript.title}
                                    </Typography>
                                  
                                    <Typography variant="body2" sx={{ mt: 0.5, color: getStatusColor(manuscript.status) }}>
                                        Status: {manuscript.status === 0 ? 'In Review' : manuscript.status === 1 ? 'Published' :  manuscript.status === 2 ? 'Rejected' : 'Pending Payment'}
                                        
                                        {manuscript.status === 1 && (
                                            <a
                                                href={`https://journal.wajls.com`}
                                                // target="_blank"
                                                // target="_blank"
                                                style={{ marginLeft: '8px', color: '#1976d2', textDecoration: 'none' }}
                                            >
                                                (View)
                                            </a>
                                        )}

                                        {manuscript.status === 2 && (
                                            <>
                                            <Button
                                                size="small"
                                                onClick={handleToggleRejectionNote}
                                                sx={{ marginLeft: '8px', color: '#1976d2' }}
                                            >
                                                (Why)
                                            </Button>

                                            <Collapse in={openRejectionNote}>
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                {manuscript.rejection_note || "No specified reason."}
                                                </Typography>
                                            </Collapse>
                                            </>
                                        )}

                                        {manuscript.status === 3 && (
                                            <>
                                            <Button
                                                size="small"
                                                onClick={() => makePayment(manuscript.id)}
                                                sx={{ marginLeft: '8px', color: '#1976d2' }}
                                            >
                                                (Pay Now)
                                            </Button>

                                            <Collapse in={openRejectionNote}>
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                {manuscript.rejection_note || "No specified reason."}
                                                </Typography>
                                            </Collapse>
                                            </>
                                        )}
                                    </Typography>
                                                                
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      Authors:{' '}
                                      {manuscript.authors.map((author, i) => (
                                        <Link
                                          key={author.id}
                                          href={`#`}
                                          underline="always"
                                          sx={{ mx: 0.5, cursor: 'pointer' }}
                                          // target="_blank"
                                          // target="_blank"
                                        >
                                          {`${author.name} (${author.email})`}
                                          {i < manuscript.authors.length - 1 && ','}
                                        </Link>
                                      ))}
                                    </Typography>
                                  
                                    {/* {manuscript.status !== '0' &&
                                      profileDetails.id === manuscript.publisher_id && (
                                        <Button
                                          variant="text"
                                          color="error"
                                          size="small"
                                          startIcon={<Delete />}
                                          sx={{ mt: 1 }}
                                          onClick={() => openDeleteDialog(manuscript.id)}
                                        >
                                          Request Deletion
                                        </Button>
                                      )} */}
                                  </Box>
                                ))}
                            </Stack>
                        )}
                        <Fab
                            style={{
                                backgroundColor: 'green',
                                position: 'fixed',
                                bottom: '16px',
                                right: '16px',
                                zIndex: 1000,
                                }}
                                aria-label="add"
                                onClick={() => navigate('/submit-manuscript')}
                        >
                            <Add style={{ color: 'white' }}/>
                    
                        </Fab>
                    </Box>
                </Stack>
            </Container>

            {/* Deletion Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Request Manuscript Deletion</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        label="Reason (Optional)"
                        value={deletionReason}
                        onChange={(e) => setDeletionReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={submitDeletionRequest} variant="contained" color="error">Submit Request</Button>
                </DialogActions>
            </Dialog>

            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            

            {status.message && <ToastMessage type={status.type} message={status.message} onClose={clearToastMessge} />}
        </Box>
    );
}

export default AuthorDashboard;
